/**
 * Error Service
 *
 * Centralized error reporting for the Ascleo client. Normalizes errors,
 * pushes to the DevErrorStore, logs structured console entries, and
 * shows toast notifications.
 *
 * This is a module singleton (not an Effect Context.Tag) so it can be
 * called from React handlers, collection callbacks, and Effect pipelines.
 *
 * @since 0.1.0
 * @example
 * ```typescript
 * import { errorService } from "@ascleo/client/services/error.service";
 *
 * errorService.report(error, { layer: "collection", source: "patient" });
 * ```
 */

import { toast } from "sonner";
import {
  isMutationError,
  isSchemaValidationError,
  isRpcError,
  isRevisionConflictError,
  isResourceAlreadyExistsError,
  isResourceNotFoundError,
  isResourceIdUnavailableError,
  isCollectionError,
  isMutationErrorUnion,
  type MutationErrorUnion,
} from "../errors/mutation.errors";
import { diagnosticLogger } from "../lib/diagnostic-logger";
import { devErrorStore, type DevErrorEntry } from "../stores/dev-error.store";

type ErrorToastOptions = NonNullable<Parameters<typeof toast.error>[1]>;

/**
 * Context for error reporting.
 *
 * @since 0.1.0
 */
export interface ErrorReportContext {
  /** Layer where the error occurred */
  layer: DevErrorEntry["layer"];
  /** Source identifier (e.g., collection name, form name, RPC method) */
  source: string;
  /** Suppress toast notification (e.g., when form handles errors inline) */
  suppressToast?: boolean;
  /** Optional user-facing message override for known domain failures */
  userMessage?: string;
  /** Optional developer/debug message override for known domain failures */
  developerMessage?: string;
  /** Optional toast action shown by the central error notification. */
  toastAction?: ErrorToastOptions["action"];
  /** Optional central error-toast duration override in milliseconds. */
  toastDurationMs?: number;
}

/**
 * Notification emitted after an error report has been normalized and stored.
 *
 * @since 2026-06-12
 * @example
 * const notification: ErrorReportNotification = { entry, context };
 */
export interface ErrorReportNotification {
  readonly entry: DevErrorEntry;
  readonly context: ErrorReportContext;
}

/**
 * Subscriber invoked for every normalized error report.
 *
 * @since 2026-06-12
 * @example
 * const unsubscribe = errorService.subscribe((notification) => console.log(notification.entry.source));
 */
export type ErrorReportSubscriber = (
  notification: ErrorReportNotification,
) => void;

const reportSubscribers = new Set<ErrorReportSubscriber>();

function notifyReportSubscribers(notification: ErrorReportNotification): void {
  for (const subscriber of reportSubscribers) {
    try {
      subscriber(notification);
    } catch (error) {
      diagnosticLogger.warn("[ErrorService] Error report subscriber failed", error);
    }
  }
}

/**
 * Extract user-facing and developer messages from an error.
 */
function extractMessages(error: unknown, overrides?: {
  readonly userMessage?: string;
  readonly developerMessage?: string;
}): {
  userMessage: string;
  developerMessage: string;
  stack?: string;
} {
  if (isMutationErrorUnion(error)) {
    return {
      userMessage: overrides?.userMessage ?? error.userMessage,
      developerMessage: overrides?.developerMessage ?? error.developerMessage,
      stack: error instanceof Error ? error.stack : undefined,
    };
  }

  if (error instanceof Error) {
    return {
      userMessage:
        overrides?.userMessage ?? "Ein unerwarteter Fehler ist aufgetreten",
      developerMessage: overrides?.developerMessage ?? error.message,
      stack: error.stack,
    };
  }

  return {
    userMessage:
      overrides?.userMessage ?? "Ein unerwarteter Fehler ist aufgetreten",
    developerMessage: overrides?.developerMessage ?? String(error),
  };
}

/**
 * Determine the error layer from a MutationErrorUnion.
 */
function inferLayer(error: unknown): DevErrorEntry["layer"] {
  if (isSchemaValidationError(error)) return "mutation";
  if (isMutationError(error)) return "mutation";
  if (
    isRpcError(error) ||
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error)
  ) {
    return "rpc";
  }
  if (isCollectionError(error)) return "collection";
  return "unknown";
}

/**
 * Determine the source from a MutationErrorUnion.
 */
function inferSource(error: unknown): string {
  if (
    isRpcError(error) ||
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error)
  ) {
    return error.rpcMethod;
  }
  if (isCollectionError(error)) return `${error.collectionId}.${error.operation}`;
  if (isSchemaValidationError(error)) return "schema-validation";
  return "unknown";
}

/**
 * A one-line, capture-safe summary of an arbitrary thrown value.
 *
 * Tagged errors frequently carry an empty `message`, so falling back to the
 * value's own shape is what makes them identifiable in a captured console.
 *
 * @since 2026-07-31
 * @example
 * ```ts
 * summarizeError(new Error("boom")); // "boom"
 * summarizeError({ _tag: "ParseError", cause: "x" }); // '{"_tag":"ParseError","cause":"x"}'
 * ```
 */
function summarizeError(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) return error.message;
  try {
    const serialized = JSON.stringify(
      error,
      (_key, value: unknown) =>
        value instanceof Error ? { name: value.name, message: value.message } : value,
    );
    return serialized === undefined ? String(error) : serialized.slice(0, 400);
  } catch {
    // A circular or unserializable payload must not take out the error report.
    return String(error);
  }
}

/**
 * Log a structured error to the console.
 */
function logStructured(error: unknown, context: ErrorReportContext): void {
  const tag = isMutationErrorUnion(error)
    ? (error as MutationErrorUnion)._tag
    : "UnknownError";

  // The tag and a short summary go in the message string, not only the payload
  // object: console capture (E2E diagnostics, remote log shipping) serializes
  // structured arguments as "Object", which made tagged errors — whose whole
  // meaning is the tag — impossible to identify from a captured run.
  diagnosticLogger.error(`[ErrorService][${context.layer}][${context.source}] Structured error [${tag}] ${summarizeError(error)}`, {
    layer: context.layer,
    source: context.source,
    tag,
    error,
    ...(isSchemaValidationError(error) && { validationErrorCount: error.errors.length }),
  });
}

/**
 * Show a toast notification for the error.
 */
function showToast(
  messages: { userMessage: string; developerMessage: string },
  context: ErrorReportContext
): void {
  toast.error(messages.userMessage, {
    ...(messages.developerMessage !== messages.userMessage
      ? { description: messages.developerMessage }
      : {}),
    duration: context.toastDurationMs ?? 5000,
    ...(context.toastAction !== undefined ? { action: context.toastAction } : {}),
  });
}

/**
 * Report an error to the error service.
 *
 * Normalizes the error, pushes to DevErrorStore, logs to console,
 * and optionally shows a toast notification.
 *
 * @since 0.1.0
 */
function report(error: unknown, context?: Partial<ErrorReportContext>): DevErrorEntry {
  const fullContext: ErrorReportContext = {
    layer: context?.layer ?? inferLayer(error),
    source: context?.source ?? inferSource(error),
    suppressToast: context?.suppressToast ?? false,
    ...(context?.userMessage !== undefined
      ? { userMessage: context.userMessage }
      : {}),
    ...(context?.developerMessage !== undefined
      ? { developerMessage: context.developerMessage }
      : {}),
    ...(context?.toastAction !== undefined
      ? { toastAction: context.toastAction }
      : {}),
    ...(context?.toastDurationMs !== undefined
      ? { toastDurationMs: context.toastDurationMs }
      : {}),
  };

  const messages = extractMessages(error, fullContext);

  // Push to dev error store
  const entry = devErrorStore.push({
    error,
    userMessage: messages.userMessage,
    developerMessage: messages.developerMessage,
    layer: fullContext.layer,
    source: fullContext.source,
    stack: messages.stack,
  });
  notifyReportSubscribers({ entry, context: fullContext });

  // Log structured console entry
  logStructured(error, fullContext);

  // Show toast unless suppressed
  if (!fullContext.suppressToast) {
    showToast(messages, fullContext);
  }

  return entry;
}

/**
 * Subscribe to normalized error reports.
 *
 * @since 2026-06-12
 * @example
 * const unsubscribe = errorService.subscribe(({ entry }) => console.log(entry.layer));
 */
function subscribe(subscriber: ErrorReportSubscriber): () => void {
  reportSubscribers.add(subscriber);
  return () => {
    reportSubscribers.delete(subscriber);
  };
}

/**
 * Singleton error service for centralized error reporting.
 *
 * @since 0.1.0
 */
export const errorService = {
  report,
  subscribe,
} as const;
